<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { projectsApi, type Project } from '@/services/api/projects';
import { useNotifier } from '@/composables/notifier';
import ProjectsSubNav from './ProjectsSubNav.vue';

const route = useRoute();
const project = ref<Project | null>(null);
const submitting = ref(false);
const notify = useNotifier();

function projectIdParam(): string {
  const raw = route.params.id;
  return Array.isArray(raw) ? (raw[0] ?? '') : raw ?? '';
}

async function reload(): Promise<void> {
  project.value = await projectsApi.get(projectIdParam());
}

async function save(): Promise<void> {
  if (!project.value || submitting.value) return;
  submitting.value = true;
  try {
    project.value = await projectsApi.update(project.value as Project & { id: number });
    notify.info('Project saved');
  } finally {
    submitting.value = false;
  }
}

onMounted(reload);
</script>

<template>
  <div>
    <ProjectsSubNav :project-id="projectIdParam()" />
    <section v-if="project" class="resource">
      <h1>Project Settings</h1>
      <form class="resource__form" @submit.prevent="save">
        <label>
          <span>Title</span>
          <input v-model="project.title" type="text" required />
        </label>
        <label class="resource__checkbox">
          <input v-model="project.is_public" type="checkbox" />
          <span>Public</span>
        </label>
        <button type="submit" :disabled="submitting">
          {{ submitting ? 'Saving…' : 'Save Settings' }}
        </button>
      </form>
    </section>
    <p v-else class="resource">Loading…</p>
  </div>
</template>

<style scoped src="@/assets/styles/resource-list.scss"></style>
<style scoped>
.resource__form {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-width: 32rem;
}
.resource__form input[type='text'] {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
}
.resource__checkbox {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.resource__form button {
  align-self: flex-start;
  padding: 0.5rem 1rem;
  background: #2563eb;
  color: #fff;
  border: 0;
  border-radius: 0.375rem;
  cursor: pointer;
}
</style>
